import { AxiosPromise } from 'axios';

import { mmHttp, MmRequestConfig } from './mmHttp';
import { buildPageQuery, createPaginationState, MmPaginationState, readPage } from './pagination';
import { getMobileBackendOrigin, isMobileBackendConfigured, MM_ENV_VAR_BACKEND_URL } from '../config/env';
import { createMisconfiguredError } from '../core/errors';
import {
  MmPhotoExport,
  MmPhotoExportCreatePayload,
  MmPhotoExportStatus,
  MmPage,
} from '../types/dto';

const EXPORTS_PATH = '/photo-exports';

const TERMINAL_STATUSES: MmPhotoExportStatus[] = ['COMPLETED', 'FAILED', 'CANCELLED', 'EXPIRED'];

function readText(value: any): string | null {
  if (typeof value === 'string' && value !== '') {
    return value;
  }

  return null;
}

function readNumber(value: any): number {
  return typeof value === 'number' && !isNaN(value) ? value : 0;
}

function readBool(value: any): boolean {
  return value === true;
}

function readStatus(value: any): MmPhotoExportStatus {
  const raw = readText(value);

  if (
    raw === 'QUEUED' ||
    raw === 'RESTORING' ||
    raw === 'PROCESSING' ||
    raw === 'COMPLETED' ||
    raw === 'FAILED' ||
    raw === 'CANCELLED' ||
    raw === 'EXPIRED'
  ) {
    return raw;
  }

  return 'QUEUED';
}

export function readPhotoExport(data: any): MmPhotoExport | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const exportId = readText(data.exportId);

  if (exportId === null) {
    return null;
  }

  return {
    exportId: exportId,
    status: readStatus(data.status),
    progress: readNumber(data.progress),
    photosTotal: readNumber(data.photosTotal),
    photosPacked: readNumber(data.photosPacked),
    bytesWritten: readNumber(data.bytesWritten),
    fileName: readText(data.fileName),
    createdAt: readText(data.createdAt),
    startedAt: readText(data.startedAt),
    finishedAt: readText(data.finishedAt),
    expiresAt: readText(data.expiresAt),
    errorCode: readText(data.errorCode),
    errorMessage: readText(data.errorMessage),
    requiresRestore: readBool(data.requiresRestore),
    estimatedReadyHours: typeof data.estimatedReadyHours === 'number' ? data.estimatedReadyHours : null,
    bytesEstimate: typeof data.bytesEstimate === 'number' ? data.bytesEstimate : null,
    isEstimatePartial: readBool(data.isEstimatePartial),
  };
}

export function isPhotoExportTerminal(status: MmPhotoExportStatus): boolean {
  return TERMINAL_STATUSES.indexOf(status) >= 0;
}

export function createPhotoExport(
  payload: MmPhotoExportCreatePayload,
  config?: MmRequestConfig,
): AxiosPromise<MmPhotoExport> {
  return mmHttp
    .post(EXPORTS_PATH, payload, Object.assign({ mmSection: 'photo-exports/create' }, config))
    .then((response) => {
      const item = readPhotoExport(response.data);

      if (item === null) {
        throw new Error('photo_export_invalid_response');
      }

      response.data = item;

      return response;
    });
}

export function fetchPhotoExports(
  pagination?: MmPaginationState,
  config?: MmRequestConfig,
): Promise<MmPage<MmPhotoExport>> {
  const pager = pagination ? pagination : createPaginationState();
  const params = buildPageQuery(pager);

  return mmHttp
    .get(EXPORTS_PATH, Object.assign({ params: params, mmSection: 'photo-exports/list' }, config))
    .then((response) => {
      const page = readPage<any>(response.data);
      const items: MmPhotoExport[] = [];

      for (let i = 0; i < page.content.length; i += 1) {
        const row = readPhotoExport(page.content[i]);

        if (row !== null) {
          items.push(row);
        }
      }

      return {
        content: items,
        totalElements: page.totalElements,
        totalKnown: page.totalKnown,
        hasMore: page.hasMore,
      };
    });
}

export function fetchPhotoExportById(exportId: string, config?: MmRequestConfig): Promise<MmPhotoExport> {
  return mmHttp
    .get(EXPORTS_PATH + '/' + encodeURIComponent(exportId), Object.assign({ mmSection: 'photo-exports/detail' }, config))
    .then((response) => {
      const item = readPhotoExport(response.data);

      if (item === null) {
        throw new Error('photo_export_invalid_response');
      }

      return item;
    });
}

export function cancelPhotoExport(exportId: string, config?: MmRequestConfig): AxiosPromise<MmPhotoExport> {
  return mmHttp
    .post(
      EXPORTS_PATH + '/' + encodeURIComponent(exportId) + '/cancel',
      {},
      Object.assign({ mmSection: 'photo-exports/cancel' }, config),
    )
    .then((response) => {
      const item = readPhotoExport(response.data);

      if (item !== null) {
        response.data = item;
      }

      return response;
    });
}

export function deletePhotoExport(exportId: string, config?: MmRequestConfig): AxiosPromise<void> {
  return mmHttp.delete(
    EXPORTS_PATH + '/' + encodeURIComponent(exportId),
    Object.assign({ mmSection: 'photo-exports/delete' }, config),
  );
}

export function fetchPhotoExportDownloadToken(
  exportId: string,
  config?: MmRequestConfig,
): Promise<{ token: string; expiresAt: string | null }> {
  return mmHttp
    .get(
      EXPORTS_PATH + '/' + encodeURIComponent(exportId) + '/download-token',
      Object.assign({ mmSection: 'photo-exports/download-token' }, config),
    )
    .then((response) => {
      const data = response.data;
      const token = data && typeof data.token === 'string' ? data.token : '';

      if (token === '') {
        throw new Error('photo_export_token_missing');
      }

      const expiresAt =
        data && typeof data.expiresAt === 'string' ? data.expiresAt : null;

      return { token: token, expiresAt: expiresAt };
    });
}

/** Ручка вне /api/admin/v1: адрес собираем от origin, относительный путь увёл бы на саму панель */
export function buildPhotoExportDownloadUrl(token: string): string {
  if (!isMobileBackendConfigured()) {
    throw createMisconfiguredError(MM_ENV_VAR_BACKEND_URL);
  }

  const origin = getMobileBackendOrigin();

  return origin + '/p/e/' + encodeURIComponent(token);
}

/**
 * Качаем навигацией браузера: у публичной ручки нет CORS-заголовков, XHR браузер отбракует
 * Имя файла ставит сервер в Content-Disposition, параметр оставлен ради вызывающих экранов
 */
export function downloadPhotoExportArchive(
  exportId: string,
  fileName: string | null,
  config?: MmRequestConfig,
): Promise<void> {
  return fetchPhotoExportDownloadToken(exportId, config).then((tokenData) => {
    const url = buildPhotoExportDownloadUrl(tokenData.token);

    openDownloadUrl(url);
  });
}

function openDownloadUrl(url: string): void {
  const link = document.createElement('a');

  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener';
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
